// ONB-PARENT: first-sign-in onboarding wizard for parents. Module entry: the gate, the server state (profiles.onboarding), the step registry and the window.HUD_ONB bridge.
// Steps (ONB-2 profile + language, the child step, ONB-3 notifications, ONB-5 co-parent, ONB-4 calendar behind a flag) register themselves in steps.js; with no step registered the wizard never shows.
import * as ST from './state.js';
import { createWizard } from './shell.js';
import { registerAll } from './steps.js';
import { GOOGLE_STEP } from './step-google.js';

const H = () => window.HUD;
const T = (k, v) => { const h = H(); return h && h.T ? h.T(k, v) : k; };
const registry = [];
let wiz = null, started = false, retryTimer = null, retries = 0;

function registerStep(def) {
  if (!def || !def.id || typeof def.mount !== 'function') throw new Error('onboarding: a step needs id and mount()');
  const i = registry.findIndex(s => s.id === def.id); if (i >= 0) registry.splice(i, 1);
  registry.push(def); registry.sort((a, b) => (a.order || 0) - (b.order || 0));
}
const hasPendingInvite = () => { try { return !!(localStorage.getItem('pendingInviteToken') || localStorage.getItem('pendingHouseholdToken')); } catch (_) { return false; } };
// QA accounts only (@resend.dev) may force the wizard on an existing account with localStorage hud_onb_force=1; a real account never can.
const forced = (s) => { try { return localStorage.getItem('hud_onb_force') === '1' && /@resend\.dev$/i.test((s && s.email) || ''); } catch (_) { return false; } };
function gateCtx(withInvite) {
  const s = H() && H().session;
  return { session: s ? { id: s.id, role: s.role, isAdmin: s.isAdmin, email: s.email, onboarding: s.onboarding || null } : null, force: forced(s), hasPendingInvite: withInvite ? hasPendingInvite() : false, steps: registry.length };
}
// True while the wizard still has something to show this account: the old first-run prompts (guided tour, push banner, co-parent popup) stay quiet then.
function pending() { try { return !wiz && ST.pending(gateCtx(false)) || !!wiz; } catch (_) { return false; } }

// profiles.onboarding: a fire-and-forget write, chained with .then so the request is really sent (lazy builder); the local session copy is updated at once
function persist(state) {
  const h = H(); const s = h && h.session; if (!s || !s.id) return;
  if (!ST.sizeOk(state)) { console.error('❌ onboarding state too large, not saved'); return; }
  s.onboarding = state;
  try {
    h._supa.from('profiles').update({ onboarding: state }).eq('id', s.id)
      .then(({ error }) => { if (error) console.error('❌ onboarding persist:', error); }, (e) => console.error('❌ onboarding persist:', e));
  } catch (e) { console.error('❌ onboarding persist:', e); }
}

function retryLater(ms) { if (retryTimer || retries >= 8) return; retries++; retryTimer = setTimeout(() => { retryTimer = null; maybeStart(); }, ms); }

// Start (or resume) the wizard if this account is in scope and nothing else is on screen. Idempotent; called after the first render and after the background refresh.
function maybeStart() {
  const h = H(); if (!h || !h.session || wiz || started) return false;
  const g = ST.gate(gateCtx(true));
  if (g.expire) { const s = h.session; persist(ST.expire(s.onboarding, new Date().toISOString())); return false; }
  if (!g.show) { if (g.defer) retryLater(3000); return false; }
  if (document.querySelector('body > .overlay, body > .modal-overlay, body > .vn2-overlay')) { retryLater(1500); return false; }   // never on top of a modal or the notes sheet
  const ctx = { session: h.session, DB: h.DB, S: h.S };
  const steps = ST.visibleSteps(registry, ctx);
  if (!steps.length) return false;
  started = true;
  let state = h.session.onboarding || null;
  if (g.resume && state) { state = ST.resumed(state, new Date().toISOString()); persist(state); }
  wiz = createWizard({
    steps, state, ctx, T, isRtl: () => !!(h._isRtl && h._isRtl()), persist,
    onClose: () => { wiz = null; try { h.re && h.re(); } catch (_) {} },
  });
  wiz.open();
  return true;
}
function relang() { if (wiz) wiz.relang(); }
// the old co-parent popup stays out of the way only while the wizard is due or done for this account (R-0150)
function suppressCoParentPopup() { try { const s = H() && H().session; return !!(wiz || (s && ST.coParentPopupSuppressed(s.onboarding))); } catch (_) { return false; } }
// Settings > Set up again: put this parent back to the fresh server state (pending) and open the wizard. Does nothing while the wizard is open.
function restart() {
  const h = H(); if (!h || !h.session || wiz) return false;
  const s = h.session; if (s.role !== 'parent' || s.id === 'demo') return false;
  persist({ v: 1, status: 'pending' });
  started = false; retries = 0;
  return maybeStart();
}

registerAll(registerStep);
export function initOnboarding() { /* the bridge below is installed at import time so index.html can query pending() from the first render */ }
window.HUD_ONB = { maybeStart, pending, relang, restart, suppressCoParentPopup, registerStep, isOpen: () => !!wiz, _test: { registry, state: ST, open: (steps, state, extra) => { const h = H(); const ctx = { session: h.session, DB: h.DB, S: h.S }; wiz = createWizard(Object.assign({ steps: ST.visibleSteps(steps, ctx), state: state || null, ctx, T, isRtl: () => !!(h._isRtl && h._isRtl()), persist, onClose: () => { wiz = null; } }, extra || {})); wiz.open(); return wiz; }, wiz: () => wiz, google: GOOGLE_STEP } };
